/**
 * Popover positioning utilities using Floating UI
 *
 * Provides viewport-aware positioning with flip, shift, and size middleware
 * to ensure popovers never overflow the viewport.
 */

import {
  computePosition,
  autoUpdate,
  flip,
  shift,
  offset,
  size,
  type Placement,
  type Middleware,
} from "@floating-ui/dom";

export type PopoverSide = "top" | "bottom" | "left" | "right";
export type PopoverAlign = "start" | "center" | "end";

export type PositionConfig = {
  side: PopoverSide;
  align: PopoverAlign;
  viewportPadding: number;
  /** Offset from the trigger element in pixels */
  offsetDistance?: number;
};

/**
 * Convert side + align to Floating UI placement
 */
export const toFloatingPlacement = (side: PopoverSide, align: PopoverAlign): Placement => {
  if (align === "center") return side;
  return `${side}-${align}` as Placement;
};

/**
 * Build middleware array for Floating UI
 */
export const buildMiddleware = (config: PositionConfig): Middleware[] => {
  const { viewportPadding, offsetDistance = 8 } = config;

  return [
    // Offset from trigger
    offset(offsetDistance),

    // Flip to opposite side if not enough space
    flip({
      padding: viewportPadding,
      fallbackPlacements: ["top", "bottom", "left", "right"],
      fallbackStrategy: "bestFit",
    }),

    // Shift along axis to stay in viewport
    shift({
      padding: viewportPadding,
      crossAxis: true,
      limiter: {
        fn: (state) => state, // Allow full shifting
        options: {},
      },
    }),

    // Constrain size to available space
    size({
      padding: viewportPadding,
      apply({ availableWidth, availableHeight, elements }) {
        const maxWidth = Math.max(200, availableWidth);
        const maxHeight = Math.max(150, availableHeight);
        Object.assign(elements.floating.style, {
          maxWidth: `${maxWidth}px`,
          maxHeight: `${maxHeight}px`,
        });
      },
    }),
  ];
};

export type PositionResult = {
  x: number;
  y: number;
  placement: Placement;
  maxHeight?: number;
};

/**
 * Compute popover position using Floating UI
 */
export const computePopoverPosition = async (
  trigger: HTMLElement,
  panel: HTMLElement,
  config: PositionConfig
): Promise<PositionResult> => {
  const placement = toFloatingPlacement(config.side, config.align);
  const middleware = buildMiddleware(config);

  const result = await computePosition(trigger, panel, {
    placement,
    middleware,
    strategy: "fixed",
  });

  return {
    x: result.x,
    y: result.y,
    placement: result.placement,
  };
};

/**
 * Set up auto-update for popover position (scroll, resize, etc.)
 * Returns cleanup function.
 */
export const setupAutoUpdate = (
  trigger: HTMLElement,
  panel: HTMLElement,
  config: PositionConfig,
  onUpdate: (result: PositionResult) => void
): (() => void) => {
  const update = async () => {
    const result = await computePopoverPosition(trigger, panel, config);
    onUpdate(result);
  };

  // Run initial position
  void update();

  // Set up auto-update for scroll/resize/layout changes
  return autoUpdate(trigger, panel, update, {
    ancestorScroll: true,
    ancestorResize: true,
    elementResize: true,
    layoutShift: true,
  });
};
